/**
 * Tenants API — super-admin CRUD over `/platform/tenants`, plus the
 * `/me/tenant` lookup that any authenticated user can call to fetch
 * their own tenant's basic details.
 * See backend tenantManagement.ts and getMyTenant.ts.
 */

import { fetchWithAdmin } from './fetchHelper';
import type { ExamVariant, WorkingDefaults } from '@padcanvas/shared';

/** The public tenant. Consumer users sit here, and so does the public school
 *  directory — a record self-governed by the people who use it, not a tenant's
 *  own awaiting one. */
export const PUBLIC_TENANT_ID = '0';

// ── Shared types (kept loose; mirror backend Tenant shape) ─────────────

export type TenantType = 'public' | 'school' | 'teacher' | 'organization' | 'personal';
export type TenantStatus = 'active' | 'suspended' | 'archived';

export interface TenantBranding {
    logoUrl?: string;
}

/** How a tenant's students give their answers on one kind of attempt —
 *  see answer-sheet-reader-target.md §4a. */
export interface AnswerSheetExpectation {
    student: 'none' | 'omr' | 'list' | 'export';
    required: boolean;
}

export interface TenantAnswerSheets {
    byModality: { practice?: AnswerSheetExpectation; test?: AnswerSheetExpectation };
}

export interface Tenant {
    id: string;
    type: TenantType;
    name: string;
    status: TenantStatus;
    /** URL label — `<slug>.buzzbrain.ai`, `buzzbrain.ai/t/<slug>`. Absent on
     *  docs written before the backfill; falls back to `id`. */
    slug?: string;
    /** Retired slugs, still resolving (then redirecting) so a rename does not
     *  break existing links. */
    previousSlugs?: string[];
    parentTenantId?: string;
    adminUserIds: string[];
    branding?: TenantBranding;
    createdAt: string;
    createdBy: string;
    updatedAt?: string;
    updatedBy?: string;
}

/** Every tenant setting, by key — mirrors the backend `TenantSettingValues`. */
export interface TenantSettingValues {
    answerSheets?: TenantAnswerSheets;
    omr?: { defaultSheetId?: string };
    /** Whether an attempt's working is marked, by modality and question class,
     *  when its paper and assignment do not say. See working-role-target.md §2. */
    working?: WorkingDefaults;
    /** Whether a diagnosis graded `low` is proposed to teachers or withheld.
     *  See diagnosis-review-target.md. */
    diagnosisReview?: { proposeLow: boolean };
    /** Whether teachers and students see the region an upload drew on a
     *  question's pages for the model. See student-work-pages-target.md §6. */
    solutionRegionDisplay?: { teacher: boolean; student: boolean };
    /** Whether a question the answer list shows correct is analysed or marked
     *  only, per modality. See answer-list-marking-target.md §3. */
    correctAnswers?: TenantCorrectAnswers;
    /** When a new paper's results reach the student, per modality — the value
     *  the create form starts at. See result-release-target.md §2. */
    resultRelease?: TenantResultRelease;
}
export type ResultRelease = 'automatic' | 'teacher';
/** Absent modality ⇒ `automatic`. */
export interface TenantResultRelease {
    byModality: { practice?: ResultRelease; test?: ResultRelease };
}
export type CorrectAnswersMode = 'analyse' | 'mark_only';
/** Absent modality ⇒ `analyse`. */
export interface TenantCorrectAnswers {
    byModality: { practice?: CorrectAnswersMode; test?: CorrectAnswersMode };
}
export type TenantSettingKey = keyof TenantSettingValues;

/** Which layer a resolved setting came from: the tenant's own doc, the
 *  "platform" tenant's defaults, or the code default. */
export type TenantSettingSource = 'tenant' | 'platform' | 'default';

/** GET /platform/tenants/{id}/settings — see backend tenantSettingsEndpoints.ts. */
export interface TenantSettingsResponse {
    tenantId: string;
    settings: { [K in TenantSettingKey]-?: { value: NonNullable<TenantSettingValues[K]>; source: TenantSettingSource } };
    /** This tenant's own gate overrides only. */
    featureGates: Record<string, boolean>;
    updatedAt?: string;
    updatedBy?: string;
}

/** A change: a key set to null goes back to inheriting; one left out is untouched. */
export interface TenantSettingsPatch {
    settings?: { [K in TenantSettingKey]?: TenantSettingValues[K] | null };
    featureGates?: Record<string, boolean | null>;
}

/** One version of a tenant's OMR sheet design, as the console shows it —
 *  see backend tenantOmrSheets.ts. Boxes are [x, y, w, h] on `page`. */
export interface TenantOmrSheet {
    sheetId: string;
    version: number;
    name: string;
    status: 'draft' | 'active' | 'retired';
    exam?: ExamVariant;
    page: [number, number];
    rows: Array<{ row: number; kind: 'choice' | 'integer'; box: [number, number, number, number]; digits?: number }>;
    idFields?: { enrolment?: string[]; testCode?: string[] };
    verification?: {
        samples: Array<{ name: string; fieldsRight: number; fieldsTotal: number; cropsRefused: number; crops: number }>;
        passed: boolean;
        verifiedAt: string;
    };
    blankSheetUrl?: string;
    createdAt: string;
    updatedAt?: string;
}

export interface TenantOmrSheetsResponse {
    defaultSheetId: string | null;
    designs: TenantOmrSheet[];
}

export interface TenantNameRow {
    id: string;
    name: string;
    type: TenantType;
    status: TenantStatus;
}

export interface TenantListResponse {
    tenants: Tenant[];
    total: number;
}

/** Minimal projection returned by `GET /me/tenant`. The synthetic
 *  super-admin tenant uses `type: 'platform'`. */
export interface MyTenantInfo {
    id: string;
    name: string;
    type: TenantType | 'platform';
    status: TenantStatus;
    branding?: TenantBranding;
}

export interface CreateTenantRequest {
    id: string;
    type: TenantType;
    name: string;
    /** Defaults to `id` server-side. */
    slug?: string;
    parentTenantId?: string;
    adminUserIds?: string[];
}

export interface UpdateTenantRequest {
    name?: string;
    status?: TenantStatus;
    /** Changing this retires the old slug into `previousSlugs[]`. */
    slug?: string;
    parentTenantId?: string | null;
    adminUserIds?: string[];
    branding?: TenantBranding;
}

// ── API ────────────────────────────────────────────────────────────────

export const tenantsApi = {
    list: (filter?: { type?: TenantType; status?: TenantStatus }): Promise<TenantListResponse> => {
        const params = new URLSearchParams();
        if (filter?.type) params.set('type', filter.type);
        if (filter?.status) params.set('status', filter.status);
        const qs = params.toString();
        return fetchWithAdmin<TenantListResponse>(`/platform/tenants${qs ? `?${qs}` : ''}`);
    },

    getById: (id: string): Promise<Tenant> =>
        fetchWithAdmin<Tenant>(`/platform/tenants/${encodeURIComponent(id)}`),

    /** GET /platform/tenant-names — id + name for every tenant, for the sidebar
     *  filter. Platform tier, so anyone who operates across tenants can scope
     *  what they see; `list` stays super-admin. */
    names: (): Promise<{ tenants: TenantNameRow[] }> =>
        fetchWithAdmin<{ tenants: TenantNameRow[] }>('/platform/tenant-names'),

    /** Basic details of the caller's own tenant. Available to every
     *  authenticated user — does not require super-admin like `list`/`getById`. */
    getMine: (): Promise<MyTenantInfo> =>
        fetchWithAdmin<MyTenantInfo>('/me/tenant'),

    create: (body: CreateTenantRequest): Promise<Tenant> =>
        fetchWithAdmin<Tenant>('/platform/tenants', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        }),

    update: (id: string, body: UpdateTenantRequest): Promise<Tenant> =>
        fetchWithAdmin<Tenant>(`/platform/tenants/${encodeURIComponent(id)}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        }),

    /** Upload a branding logo for a tenant (public blob). Returns its URL, which
     *  the caller then persists via `update(id, { branding: { logoUrl } })`. */
    uploadLogo: (tenantId: string, imageBase64: string, contentType: string): Promise<{ url: string; path: string }> =>
        fetchWithAdmin<{ url: string; path: string }>('/media/upload', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ purpose: 'tenant-logo', imageBase64, contentType, ref: { tenantId } }),
        }),

    /** GET /platform/tenants/{id}/settings — every setting resolved, with its
     *  layer, plus the tenant's own gate overrides. */
    settings: (id: string): Promise<TenantSettingsResponse> =>
        fetchWithAdmin<TenantSettingsResponse>(`/platform/tenants/${encodeURIComponent(id)}/settings`),

    updateSettings: (id: string, patch: TenantSettingsPatch): Promise<TenantSettingsResponse> =>
        fetchWithAdmin<TenantSettingsResponse>(`/platform/tenants/${encodeURIComponent(id)}/settings`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(patch),
        }),

    /** GET /platform/tenants/{id}/omr-sheets — every version of every OMR
     *  sheet design the tenant has. Read-only: designs are saved by the ops script. */
    omrSheets: (id: string): Promise<TenantOmrSheetsResponse> =>
        fetchWithAdmin<TenantOmrSheetsResponse>(`/platform/tenants/${encodeURIComponent(id)}/omr-sheets`),

    archive: (id: string): Promise<{ id: string; status: TenantStatus }> =>
        fetchWithAdmin<{ id: string; status: TenantStatus }>(`/platform/tenants/${encodeURIComponent(id)}`, {
            method: 'DELETE',
        }),

    hardDelete: (
        id: string,
        body: HardDeleteRequest,
        opts: { dryRun: boolean },
    ): Promise<HardDeleteResponse> =>
        fetchWithAdmin<HardDeleteResponse>(
            `/platform/tenants/${encodeURIComponent(id)}/hard-delete?dryRun=${opts.dryRun ? 'true' : 'false'}`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            },
        ),
};

// ── Hard-delete types ──────────────────────────────────────────────────

export interface HardDeleteRequest {
    confirmTenantId: string;
    allowProd?: boolean;
    allowUsers?: boolean;
    allowActivity?: boolean;
    allowLarge?: boolean;
}

export interface HardDeleteVetoes {
    reservedTenant: boolean;
    notArchived: boolean;
    prodEndpoint: boolean;
    registeredUsers: boolean;
    hasActivity: boolean;
    largeCascade: boolean;
}

export interface HardDeletePlan {
    userIds: string[];
    registeredUserCount: number;
    /** Subset of registeredUserCount excluded from the user/activity vetoes. */
    testUserCount: number;
    perContainerCounts: Record<string, number>;
    totalCount: number;
}

export interface HardDeleteResult {
    perContainerDeleted: Record<string, number>;
    usersDeleted: number;
    tenantDeleted: boolean;
    totalDeleted: number;
    errors: { container: string; id: string; error: string }[];
}

export interface HardDeleteResponse {
    tenantId: string;
    dryRun: boolean;
    vetoes: HardDeleteVetoes;
    plan: HardDeletePlan;
    result?: HardDeleteResult;
    auditLogId: string;
}
